import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Table from '@mui/joy/Table';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import DialogActions from '@mui/joy/DialogActions';
import Grid from '@mui/joy/Grid';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import { productsApi } from '../../api/products.api';
import { attributeSetsApi } from '../../api/attribute-sets.api';
import { Product, ProductAttributeValue, CreateProductVariantDto } from '../../types/product';
import { AttributeSet, AttributeSetItem } from '../../types/attribute-set';
import { Attribute } from '../../types/attribute';

const ProductDetail: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attributeSet, setAttributeSet] = useState<AttributeSet | null>(null);
  const [variantAxisAttributes, setVariantAxisAttributes] = useState<AttributeSetItem[]>([]);
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [variants, setVariants] = useState<Product[]>([]);

  const [variantData, setVariantData] = useState<Record<string, string>>({
    sku: '',
    price: '',
    quantity: '',
  });

  useEffect(() => {
    loadProduct();
  }, [reference]);

  const loadProduct = async () => {
    if (!reference) return;
    try {
      setLoading(true);
      setError(null);
      const data = await productsApi.getOne(reference);
      setProduct(data);

      if (data.children && data.children.length > 0) {
        setVariants(data.children);
      }

      if (data.category?.attributeSet) {
        setAttributeSet(data.category.attributeSet);
        const variantItems = data.category.attributeSet.items?.filter(
          (item: AttributeSetItem) => item.attribute?.isVariantAxis
        ) || [];
        setVariantAxisAttributes(variantItems);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVariant = async () => {
    if (!product || product.productType !== 'configurable') return;

    try {
      setSubmitting(true);

      const attributeValues: ProductAttributeValue[] = [];
      Object.entries(variantData).forEach(([attrKey, value]) => {
        if (value && attrKey !== 'sku' && attrKey !== 'price' && attrKey !== 'quantity') {
          const attrItem = variantAxisAttributes.find(
            (item) => item.attribute?.code === attrKey
          );
          if (attrItem?.attribute && attrItem.attribute.id) {
            attributeValues.push({
              attributeId: attrItem.attribute.id,
              valueText: value,
              valueNumber: undefined,
              valueBoolean: undefined,
              valueDate: undefined,
              valueOptionId: undefined,
            });
          }
        }
      });

      const variantDto: CreateProductVariantDto = {
        sku: variantData.sku || undefined,
        price: parseFloat(variantData.price) || undefined,
        quantity: parseInt(variantData.quantity, 10) || undefined,
        attributeValues,
      };

      await productsApi.createVariant(product.reference, variantDto);
      setShowVariantForm(false);
      setVariantData({ sku: '', price: '', quantity: '' });
      await loadProduct();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear la variante');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateCombinations = async () => {
    if (!product || variantAxisAttributes.length === 0) return;

    try {
      setSubmitting(true);

      const axes = variantAxisAttributes
        .filter((item) => item.attribute?.dataType === 'select')
        .map((item) => ({
          attribute: item.attribute!,
          options: item.attribute!.options || [],
        }));

      if (axes.length === 0) {
        setError('No hay atributos de tipo Select para generar combinaciones');
        return;
      }

      const combinations = generateCombinations(axes.map((a) => a.options));

      for (const combo of combinations) {
        const attributeValues: ProductAttributeValue[] = combo.map((optionId, idx) => {
          const attrId = axes[idx].attribute.id;
          if (!attrId) {
            throw new Error(`Atributo sin ID en índice ${idx}`);
          }
          return {
            attributeId: attrId,
            valueText: undefined,
            valueNumber: undefined,
            valueBoolean: undefined,
            valueDate: undefined,
            valueOptionId: optionId,
          };
        });

        const variantDto: CreateProductVariantDto = {
          sku: `${product.sku}-${combo.join('-')}`,
          price: product.price,
          quantity: 0,
          attributeValues,
        };

        await productsApi.createVariant(product.reference, variantDto);
      }

      await loadProduct();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar combinaciones');
    } finally {
      setSubmitting(false);
    }
  };

  const generateCombinations = (optionArrays: any[][]): number[][] => {
    if (optionArrays.length === 0) return [[]];
    if (optionArrays.length === 1) return optionArrays[0].map((opt) => [opt.id]);

    const firstOptions = optionArrays[0];
    const restCombinations = generateCombinations(optionArrays.slice(1));

    return firstOptions.flatMap((option) =>
      restCombinations.map((combo) => [option.id, ...combo])
    );
  };

  if (loading) {
    return (
      <PageStack>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </PageStack>
    );
  }

  if (!product) {
    return (
      <PageStack>
        <Alert color="danger">Producto no encontrado</Alert>
        <Button startDecorator={<ArrowBackRoundedIcon />} onClick={() => navigate('/products')}>
          Volver a productos
        </Button>
      </PageStack>
    );
  }

  return (
    <PageStack>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <IconButton variant="plain" onClick={() => navigate('/products')}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <DashboardPageHeader title={product.name} />
        </Box>
        <Button
          variant="plain"
          startDecorator={<EditRoundedIcon />}
          onClick={() => navigate(`/products/${product.reference}/edit`)}
        >
          Editar
        </Button>
      </Stack>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid xs={12} sm={6}>
          <Sheet
            variant="outlined"
            sx={{
              borderRadius: 'md',
              p: 2,
            }}
          >
            <Typography level="title-md" sx={{ mb: 2 }}>
              Información General
            </Typography>
            <Stack spacing={1.5}>
              <Box>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  SKU
                </Typography>
                <Typography level="body-md">{product.sku || '-'}</Typography>
              </Box>
              <Box>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Tipo de Producto
                </Typography>
                <Typography level="body-md">
                  {product.productType === 'configurable'
                    ? 'Compuesto (variantes)'
                    : 'Único'}
                </Typography>
              </Box>
              {product.productType === 'simple' && (
                <>
                  <Box>
                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                      Precio
                    </Typography>
                    <Typography level="body-md">${product.price.toFixed(2)}</Typography>
                  </Box>
                  <Box>
                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                      Cantidad
                    </Typography>
                    <Typography level="body-md">{product.quantity}</Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Sheet>
        </Grid>

        {product.productType === 'configurable' && (
          <Grid xs={12} sm={6}>
            <Sheet
              variant="outlined"
              sx={{
                borderRadius: 'md',
                p: 2,
              }}
            >
              <Typography level="title-md" sx={{ mb: 2 }}>
                Gestión de Variantes
              </Typography>
              <Stack spacing={1.5}>
                <Typography level="body-sm">
                  {variants.length} variante{variants.length !== 1 ? 's' : ''} creada
                  {variants.length !== 1 ? 's' : ''}
                </Typography>
                <Button
                  variant="soft"
                  color="primary"
                  startDecorator={<AddRoundedIcon />}
                  onClick={() => setShowVariantForm(true)}
                >
                  Agregar Variante
                </Button>
                {variantAxisAttributes.length > 0 && (
                  <Button
                    variant="soft"
                    startDecorator={<AutoAwesomeIcon />}
                    onClick={handleGenerateCombinations}
                    disabled={submitting}
                  >
                    Generar Combinaciones
                  </Button>
                )}
                {variantAxisAttributes.length === 0 && (
                  <Typography level="body-sm" sx={{ color: 'warning.500' }}>
                    Esta categoría no tiene atributos de variación configurados. Configúralos en las características de la categoría.
                  </Typography>
                )}
              </Stack>
            </Sheet>
          </Grid>
        )}
      </Grid>

      {variants.length > 0 && (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'md',
            overflow: 'auto',
            mt: 3,
          }}
        >
          <Table size="sm" hoverRow>
            <thead>
              <tr>
                <th style={{ width: '25%' }}>SKU</th>
                <th style={{ width: '15%' }}>Precio</th>
                <th style={{ width: '15%' }}>Cantidad</th>
                <th style={{ width: '35%' }}>Atributos</th>
                <th style={{ width: '10%' }} align="center">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => (
                <tr key={variant.id}>
                  <td>{variant.sku || '-'}</td>
                  <td>${variant.price.toFixed(2)}</td>
                  <td>{variant.quantity}</td>
                  <td>
                    {variant.attributeValues && variant.attributeValues.length > 0 ? (
                      <Stack spacing={0.5}>
                        {variant.attributeValues.map((av, idx) => (
                          <Typography key={idx} level="body-xs">
                            {av.attribute?.name}: {av.valueText || av.valueOption?.label || '-'}
                          </Typography>
                        ))}
                      </Stack>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td align="center">
                    <IconButton
                      size="sm"
                      variant="plain"
                      onClick={() => navigate(`/products/${variant.reference}/edit`)}
                    >
                      <EditRoundedIcon />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      <Modal open={showVariantForm} onClose={() => setShowVariantForm(false)}>
        <ModalDialog size="sm">
          <DialogTitle>Agregar Variante</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>SKU</FormLabel>
                <Input
                  value={variantData.sku}
                  onChange={(e) => setVariantData({ ...variantData, sku: e.target.value })}
                  placeholder="Ej: PALO-001-M"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Precio</FormLabel>
                <Input
                  type="number"
                  value={variantData.price}
                  onChange={(e) => setVariantData({ ...variantData, price: e.target.value })}
                  placeholder="Ej: 100.00"
                  slotProps={{
                    input: {
                      step: 0.01,
                    },
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Cantidad</FormLabel>
                <Input
                  type="number"
                  value={variantData.quantity}
                  onChange={(e) => setVariantData({ ...variantData, quantity: e.target.value })}
                  placeholder="Ej: 50"
                />
              </FormControl>

              {variantAxisAttributes.map((item) => {
                const attr = item.attribute;
                if (!attr) return null;

                return (
                  <FormControl key={attr.id}>
                    <FormLabel>{attr.name}</FormLabel>
                    {attr.dataType === 'select' ? (
                      <Select
                        value={variantData[attr.code] || ''}
                        onChange={(_, val) =>
                          setVariantData({ ...variantData, [attr.code]: val || '' })
                        }
                      >
                        {attr.options?.map((opt) => (
                          <Option key={opt.id} value={opt.id.toString()}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        value={variantData[attr.code] || ''}
                        onChange={(e) =>
                          setVariantData({ ...variantData, [attr.code]: e.target.value })
                        }
                        placeholder={attr.name}
                      />
                    )}
                  </FormControl>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setShowVariantForm(false)}>
              Cancelar
            </Button>
            <Button
              loading={submitting}
              onClick={handleAddVariant}
              disabled={!variantData.sku || !variantData.price || !variantData.quantity}
            >
              Crear Variante
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </PageStack>
  );
};

export default ProductDetail;
